import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@shared/ui';

type Props = {
  imageUrl: string;
  link: string;
  preview?: boolean;
  onOpen?: () => void | Promise<void>;
};

type SafeLink = { href: string; external: boolean };

const ASCII_CONTROL_MAX = 0x1f;
const ASCII_DELETE = 0x7f;

function containsUnsafeLinkCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      character === '\\' ||
      codePoint === undefined ||
      codePoint <= ASCII_CONTROL_MAX ||
      codePoint === ASCII_DELETE
    ) {
      return true;
    }
  }
  return false;
}

function resolveSafeLink(value: string): SafeLink | null {
  const link = value.trim();
  if (containsUnsafeLinkCharacter(link)) return null;
  if (link.startsWith('/') && !link.startsWith('//')) {
    return { href: link, external: false };
  }
  try {
    const url = new URL(link);
    return url.protocol === 'https:' ? { href: url.href, external: true } : null;
  } catch {
    return null;
  }
}

export function AdPopupArtwork({ imageUrl, link, preview = false, onOpen }: Props) {
  const { t } = useTranslation();

  const open = () => {
    if (preview) return;
    const safeLink = resolveSafeLink(link);
    if (safeLink === null) return;
    void onOpen?.();
    if (safeLink.external) {
      window.open(safeLink.href, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.assign(safeLink.href);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <img
        src={imageUrl}
        alt={t('adPopups.runtime.imageAlt')}
        className="max-h-[70dvh] w-full object-contain"
      />
      <div className="flex items-center justify-between gap-3 p-3">
        <Text size="sm" color="muted" className="min-w-0 truncate">
          {link}
        </Text>
        <Button type="button" size="sm" disabled={preview} onClick={open}>
          <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
          {preview ? t('adPopups.runtime.preview') : t('adPopups.runtime.open')}
        </Button>
      </div>
    </div>
  );
}
